import { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, Calendar, UserPlus, CreditCard,
  User, LogOut, Settings, ChevronRight, Loader2, Users, Network,
  ArrowLeft, MessageCircle, Bot,
} from 'lucide-react';
import { useIkalStore } from '@/store/ikal.store';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import api from '@/lib/axios';
import { useAuthStore } from '@/store/auth.store';
import { MODULES, ROUTE_MODULE_MAP } from './modules.config';
import { NuevoPacienteDialog } from '@/components/pacientes/NuevoPacienteDialog';
import { CitaFormDialog } from '@/pages/agenda/CitaFormDialog';
import { toast } from 'sonner';
import { NotificacionesBell } from '@/components/notificaciones/NotificacionesBell';
import { useChatStore } from '@/store/chat.store';
import { useSocket } from '@/hooks/useSocket';
import { useTareasRealtime } from '@/hooks/useTareasRealtime';
import logoHorizontal from '@/assets/logo-horizontal-bk.svg';
import { getErrorMessage } from '@/lib/errors';

// ── Helpers ───────────────────────────────────────────────────────────────────

function useDebounce<T>(value: T, delay: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

function getInitialsLocal(name: string) {
  return name.split(' ').map(w => w[0] ?? '').join('').toUpperCase().slice(0, 2);
}

// ── Buscador global ───────────────────────────────────────────────────────────

interface GlobalSearchProps {
  autoFocus?: boolean;
}

function GlobalSearch({ autoFocus }: GlobalSearchProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const debouncedQ = useDebounce(query, 280);
  const active = debouncedQ.trim().length >= 2;

  const { data: pacientes = [], isFetching: loadingP } = useQuery({
    queryKey: ['search-pacientes', debouncedQ],
    queryFn: () => api.get(`/api/pacientes?search=${encodeURIComponent(debouncedQ)}`).then(r => r.data.slice(0, 5)),
    enabled: active,
    staleTime: 15_000,
  });

  const { data: citas = [], isFetching: loadingC } = useQuery({
    queryKey: ['search-citas', debouncedQ],
    queryFn: () => api.get(`/api/citas?search=${encodeURIComponent(debouncedQ)}`).then(r => r.data.slice(0, 4)),
    enabled: active,
    staleTime: 15_000,
  });

  const { data: medicos = [], isFetching: loadingM } = useQuery({
    queryKey: ['search-red', debouncedQ],
    queryFn: () => api.get(`/api/red/directorio?q=${encodeURIComponent(debouncedQ)}`).then(r => r.data.slice(0, 4)),
    enabled: active,
    staleTime: 15_000,
  });

  const loading = loadingP || loadingC || loadingM;
  const hasResults = (pacientes as any[]).length + (citas as any[]).length + (medicos as any[]).length > 0;
  const showPanel = open && active;

  useEffect(() => {
    function onClickOut(e: MouseEvent) {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOut);
    return () => document.removeEventListener('mousedown', onClickOut);
  }, []);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => {
    if (autoFocus) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [autoFocus]);

  function goAndClose(path: string) {
    navigate(path);
    setOpen(false);
    setQuery('');
  }

  return (
    <div ref={wrapperRef} className="relative w-full md:max-w-sm">
      <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-gray-400" />
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => { setQuery(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Buscar pacientes, citas, médicos..."
        className="w-full h-11 pl-10 pr-10 rounded-2xl text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all"
        style={{ backgroundColor: open ? '#e8eef4' : '#f1f5f9', border: 'none' }}
      />
      {loading && active ? (
        <Loader2 size={13} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 animate-spin" />
      ) : (
        <kbd className="absolute right-3 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-medium pointer-events-none"
          style={{ backgroundColor: '#e2e8f0', color: '#94a3b8' }}>
          ⌘K
        </kbd>
      )}

      {/* Panel de resultados */}
      {showPanel && (
        <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-2xl shadow-xl border border-gray-100 z-50 overflow-hidden">
          {!hasResults && !loading && (
            <p className="px-4 py-3 text-sm text-gray-400 text-center">Sin resultados para "{debouncedQ}"</p>
          )}

          {/* Pacientes */}
          {(pacientes as any[]).length > 0 && (
            <div>
              <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
                <Users size={12} className="text-primary-400" />
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Pacientes</p>
              </div>
              {(pacientes as any[]).map((p: any) => (
                <button key={p.id} onClick={() => goAndClose(`/pacientes/${p.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-xs font-bold flex-shrink-0">
                    {getInitialsLocal(`${p.nombre} ${p.apellidoPaterno}`)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">{p.nombre} {p.apellidoPaterno} {p.apellidoMaterno}</p>
                    {p.telefono && <p className="text-xs text-gray-400 truncate">{p.telefono}</p>}
                  </div>
                  <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Citas */}
          {(citas as any[]).length > 0 && (
            <div className={(pacientes as any[]).length > 0 ? 'border-t border-gray-50' : ''}>
              <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
                <Calendar size={12} className="text-violet-400" />
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Citas</p>
              </div>
              {(citas as any[]).map((c: any) => (
                <button key={c.id} onClick={() => goAndClose(`/agenda`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group">
                  <div className="w-8 h-8 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                    <Calendar size={13} className="text-violet-500" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">
                      {c.paciente ? `${c.paciente.nombre} ${c.paciente.apellidoPaterno}` : 'Cita'}
                    </p>
                    <p className="text-xs text-gray-400 truncate">
                      {c.fechaInicio ? format(new Date(c.fechaInicio), "d MMM · HH:mm", { locale: es }) : ''}
                      {c.motivo ? ` · ${c.motivo}` : ''}
                    </p>
                  </div>
                  <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          {/* Médicos red */}
          {(medicos as any[]).length > 0 && (
            <div className={(pacientes as any[]).length + (citas as any[]).length > 0 ? 'border-t border-gray-50' : ''}>
              <div className="flex items-center gap-2 px-4 pt-3 pb-1.5">
                <Network size={12} className="text-cyan-400" />
                <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide">Red médica</p>
              </div>
              {(medicos as any[]).map((m: any) => (
                <button key={m.id} onClick={() => goAndClose(`/red/directorio`)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors text-left group">
                  <div className="w-8 h-8 rounded-full bg-cyan-50 flex items-center justify-center text-cyan-600 text-xs font-bold flex-shrink-0">
                    {getInitialsLocal(m.nombre ?? 'M')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-800 truncate">Dr. {m.nombre}</p>
                    <p className="text-xs text-gray-400 truncate">
                      {m.especialidad?.nombre}{m.clinica?.ciudad ? ` · ${m.clinica.ciudad}` : ''}
                    </p>
                  </div>
                  <ChevronRight size={13} className="text-gray-300 group-hover:text-gray-400 flex-shrink-0" />
                </button>
              ))}
            </div>
          )}

          <div className="h-2" />
        </div>
      )}
    </div>
  );
}

// ── Topbar principal ──────────────────────────────────────────────────────────

export function Topbar() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { usuario, logout } = useAuthStore();
  const { openChat } = useChatStore();
  const { openIkal } = useIkalStore();
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);

  useSocket();
  useTareasRealtime();

  const [nuevaCitaOpen, setNuevaCitaOpen] = useState(false);
  const [nuevoPacienteOpen, setNuevoPacienteOpen] = useState(false);

  const { data: stats } = useQuery<Record<string, any>>({
    queryKey: ['stats'],
    queryFn: () => api.get('/api/stats').then(r => r.data),
    staleTime: 60_000,
  });

  const { data: profile } = useQuery({
    queryKey: ['auth-me'],
    queryFn: () => api.get('/api/auth/me').then(r => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const { data: pacientes = [] } = useQuery({
    queryKey: ['pacientes'],
    queryFn: () => api.get('/api/pacientes').then(r => r.data),
    enabled: nuevaCitaOpen,
    staleTime: 60_000,
  });
  const { data: medicos = [] } = useQuery({
    queryKey: ['medicos-select'],
    queryFn: () => api.get('/api/medicos').then(r => r.data),
    enabled: nuevaCitaOpen,
    staleTime: 60_000,
  });
  const { data: clinicas = [] } = useQuery({
    queryKey: ['clinicas'],
    queryFn: () => api.get('/api/clinicas').then(r => r.data),
    enabled: nuevaCitaOpen,
    staleTime: 60_000,
  });

  const createCitaMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/citas', data).then(r => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['citas'] });
      toast.success('Cita agendada');
      setNuevaCitaOpen(false);
    },
    onError: (err: any) => toast.error(getErrorMessage(err, 'Error al agendar la cita')),
  });

  const fotoUrl = profile?.fotoUrl || usuario?.fotoUrl || null;
  const fotoSrc = fotoUrl
    ? `${fotoUrl}?v=${profile?.updatedAt ? new Date(profile.updatedAt).getTime() : 0}`
    : null;
  const initial = usuario?.nombre?.charAt(0)?.toUpperCase() || 'U';

  async function handleLogout() {
    try { await api.post('/api/auth/logout'); } catch { /* ignore */ }
    qc.clear();
    logout();
    navigate('/login');
  }

  return (
    <>
      <header
        className="h-14 md:h-20 w-full bg-white md:rounded-[2rem] flex items-center px-4 md:px-8 flex-shrink-0 gap-3"
        style={{ border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}
      >
        {/* ── Mobile: botón atrás al buscar ── */}
        {mobileSearchOpen && (
          <button
            className="md:hidden p-1.5 -ml-1 flex-shrink-0 text-gray-500"
            onClick={() => setMobileSearchOpen(false)}
            aria-label="Cerrar búsqueda"
          >
            <ArrowLeft size={20} />
          </button>
        )}

        {/* ── Mobile: logo (oculto al buscar) ── */}
        {!mobileSearchOpen && (
          <div className="flex md:hidden items-center flex-shrink-0">
            <img
              src={logoHorizontal}
              alt="Anik"
              className="h-7 object-contain object-left"
              style={{ maxWidth: 80 }}
            />
          </div>
        )}

        {/* ── Mobile: buscador expandido ── */}
        {mobileSearchOpen && (
          <div className="flex md:hidden flex-1 min-w-0">
            <GlobalSearch autoFocus />
          </div>
        )}

        {/* ── Desktop: buscador ── */}
        <div className="hidden md:flex items-center gap-8 flex-1 max-w-2xl">
          <GlobalSearch />
        </div>

        {/* ── Acciones (ocultas al buscar en mobile) ── */}
        {!mobileSearchOpen && (
          <div className="flex items-center gap-1.5 md:gap-3 ml-auto flex-shrink-0">

            {/* Mobile: Ikal */}
            <button
              className="md:hidden p-2 rounded-xl hover:bg-gray-100 transition-colors"
              style={{ color: '#568f88' }}
              onClick={() => openIkal()}
              aria-label="Asistente Ikal"
            >
              <Bot size={19} />
            </button>

            {/* Mobile: chat */}
            <button
              className="md:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"
              onClick={() => openChat('')}
              aria-label="Mensajes"
            >
              <MessageCircle size={19} />
            </button>

            {/* Mobile: ícono de búsqueda */}
            <button
              className="md:hidden p-2 rounded-xl hover:bg-gray-100 text-gray-400 transition-colors"
              onClick={() => setMobileSearchOpen(true)}
              aria-label="Buscar"
            >
              <Search size={19} />
            </button>

            {/* Desktop: botón Nuevo */}
            <div className="hidden md:block">
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    className="flex items-center gap-1.5 px-4 py-2 text-white text-sm font-medium rounded-xl transition-colors"
                    style={{ backgroundColor: '#0e85bf' }}
                    onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#0c76aa'; }}
                    onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#0e85bf'; }}
                  >
                    <Plus size={14} strokeWidth={2.5} />
                    Nuevo
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content align="end" sideOffset={6}
                    className="z-50 min-w-[200px] bg-white rounded-xl shadow-lg border border-gray-100 py-1.5">
                    <DropdownMenu.Item
                      onSelect={() => setNuevaCitaOpen(true)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                    >
                      <div className="w-6 h-6 rounded-md bg-primary-50 flex items-center justify-center flex-shrink-0">
                        <Calendar size={13} className="text-primary-600" />
                      </div>
                      Nueva cita
                    </DropdownMenu.Item>
                    <DropdownMenu.Item
                      onSelect={() => setNuevoPacienteOpen(true)}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                    >
                      <div className="w-6 h-6 rounded-md bg-green-50 flex items-center justify-center flex-shrink-0">
                        <UserPlus size={13} className="text-green-600" />
                      </div>
                      Nuevo paciente
                    </DropdownMenu.Item>
                    <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />
                    <DropdownMenu.Item
                      onSelect={() => navigate('/finanzas')}
                      className="flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                    >
                      <div className="w-6 h-6 rounded-md bg-orange-50 flex items-center justify-center flex-shrink-0">
                        <CreditCard size={13} className="text-orange-500" />
                      </div>
                      Registrar cobro
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            </div>

            {/* Campana — visible en ambos */}
            <NotificacionesBell onAbrirChat={openChat} />

            {/* Separador — solo desktop */}
            <div className="hidden md:block w-px h-8 bg-gray-200" />

            {/* Avatar con dropdown */}
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button className="flex items-center gap-3 pl-2 md:pl-3 pr-1.5 py-1.5 rounded-2xl transition-colors hover:bg-gray-50 outline-none">
                  <span className="text-sm font-semibold text-gray-800 hidden md:block">{usuario?.nombre}</span>
                  <div className="relative w-8 h-8 md:w-9 md:h-9 rounded-full border-2 border-white overflow-hidden flex-shrink-0 shadow-md">
                    <div
                      className="w-full h-full flex items-center justify-center text-white text-sm font-bold"
                      style={{ backgroundColor: '#0e85bf' }}
                    >
                      {initial}
                    </div>
                    {fotoSrc && (
                      <img
                        src={fotoSrc}
                        alt={usuario?.nombre}
                        className="absolute inset-0 w-full h-full object-cover"
                        onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                  </div>
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={8}
                  className="z-50 w-56 bg-white rounded-xl shadow-lg border border-gray-100 py-1.5 outline-none">

                  <div className="px-3 py-2.5 border-b border-gray-100 mb-1">
                    <p className="text-sm font-semibold text-gray-800 truncate">{usuario?.nombre}</p>
                    <p className="text-xs text-gray-400 truncate">{usuario?.email}</p>
                  </div>

                  <DropdownMenu.Item
                    onSelect={() => navigate('/mi-perfil')}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                  >
                    <User size={14} className="text-gray-400" />
                    Mi perfil
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    onSelect={() => navigate('/configuracion')}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-text-primary hover:bg-gray-50 cursor-pointer outline-none"
                  >
                    <Settings size={14} className="text-gray-400" />
                    Configuración
                  </DropdownMenu.Item>

                  <DropdownMenu.Separator className="my-1 h-px bg-gray-100" />

                  <DropdownMenu.Item
                    onSelect={handleLogout}
                    className="flex items-center gap-2.5 px-3 py-2 text-sm text-red-600 hover:bg-red-50 cursor-pointer outline-none"
                  >
                    <LogOut size={14} />
                    Cerrar sesión
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        )}
      </header>

      {/* Modales globales */}
      <NuevoPacienteDialog open={nuevoPacienteOpen} onOpenChange={setNuevoPacienteOpen} />
      <CitaFormDialog
        open={nuevaCitaOpen}
        onOpenChange={setNuevaCitaOpen}
        pacientes={pacientes as any[]}
        medicos={medicos as any[]}
        clinicas={clinicas as any[]}
        onSubmit={data => createCitaMutation.mutate(data)}
        isSubmitting={createCitaMutation.isPending}
      />
    </>
  );
}
